import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import {
  GroupType,
  SignalsGroupsType,
} from "@shared/features/onboarding/types";
import { useCallback, useMemo } from "react";
import { MARGIN_EXCHANGES } from "@shared/common/constants";
import { BotStatus, BotType, GroupData, SortingOrder } from "@shared/types";
import {
  sortByActivity,
  sortGroupsExpiration,
} from "@shared/features/configuration/utils";
import { sortByStringProperty } from "@shared/utils/general";
import { uniq } from "lodash";
import { useClientsData } from "@shared/utils/hooks/misc";
import { selectBotsData } from "@shared/slices/bots.slice";
import { SPOT_BOT_FUTURES_EXCHANGES } from "@shared/features/bots/BotForm/common/futuresAsSpot";

interface useFollowSignalsGroupProps {
  isGroupsPage?: boolean;
  discoveryExchange?: string;
}

const useFollowSignalsGroup = ({
  isGroupsPage,
  discoveryExchange,
}: useFollowSignalsGroupProps) => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const { activeClientsData } = useClientsData();

  const exchanges = useMemo(
    () =>
      uniq(
        (discoveryExchange
          ? [discoveryExchange]
          : activeClientsData?.map((accountData) => accountData.exchange) || []
        ).flatMap((exchange) => [
          exchange,
          ...(!isGroupsPage ? SPOT_BOT_FUTURES_EXCHANGES[exchange] || [] : []),
        ])
      ),
    [activeClientsData, discoveryExchange, isGroupsPage]
  );
  const hasLeverageExchange = exchanges.some((exchange) =>
    MARGIN_EXCHANGES.has(exchange)
  );
  const getExchangeGroupsArgs = useMemo(
    () =>
      exchanges?.length
        ? {
            exchanges,
          }
        : skipToken,
    [exchanges]
  );

  const getMyGroupsQuery = sharedLib.sharedApi.settings.useGetGroupsDataQuery(
    user?.hasExternalAccount() || user?.isAssetManager() || user?.is_whop_user
      ? undefined
      : skipToken
  );

  const {
    data: groups,
    isFetching: getGroupsFetching,
    isLoading: getGroupsLoading,
    isSuccess: getGroupsSuccess,
    refetch: getGroupsRefetch,
  } = sharedLib.sharedApi.configuration.useGetExchangeChannelsInformationQuery(
    getExchangeGroupsArgs
  );

  const getBotsDataArgs = useMemo(
    () => ({
      bot_type: BotType.SIGNAL,
      include_archived: false,
    }),
    []
  );
  sharedLib.sharedApi.bots.useGetBotsDataQuery(
    isGroupsPage ? getBotsDataArgs : skipToken
  );
  const selector = useMemo(
    () => selectBotsData(getBotsDataArgs),
    [getBotsDataArgs]
  );
  const signalsBots = useSelector(selector);

  const myGroupsIds = useMemo(
    () => new Set<number>(getMyGroupsQuery.data?.map((group) => group.id)),
    [getMyGroupsQuery]
  );

  const groupIdToNumBots = useMemo(() => {
    return (
      signalsBots
        ?.filter((bot) => bot.status === BotStatus.ACTIVE)
        .reduce((acc: Record<number, number>, bot) => {
          const groupId = bot.sub_group_id;
          if (groupId) {
            acc[groupId] = (acc[groupId] || 0) + 1;
          }
          return acc;
        }, {}) || {}
    );
  }, [signalsBots]);

  const getNumBots = (group: GroupData) => groupIdToNumBots?.[group.id] || 0;

  const groupsSort = (a: GroupData, b: GroupData) => {
    return (
      +!!b.is_active - +!!a.is_active ||
      +!!b.allow_follow - +!!a.allow_follow ||
      sortGroupsExpiration(a, b) ||
      sortByActivity(a, b, getNumBots, SortingOrder.DESCEND, false) ||
      sortByStringProperty(a, b, "name")
    );
  };

  const myGroups: GroupType[] | undefined = useMemo(
    () =>
      getMyGroupsQuery.data
        ?.filter((group) => !group.is_personal)
        ?.map((group) => ({
          ...group,
          group_type: SignalsGroupsType.MY_GROUPS,
        }))
        .sort(groupsSort),
    [getMyGroupsQuery.data]
  );

  const personalGroups = useMemo(
    () =>
      getMyGroupsQuery.data
        ?.filter((group) => group.is_personal)
        .map((group) => ({
          ...group,
          group_type: SignalsGroupsType.PERSONAL,
        })),
    [getMyGroupsQuery.data]
  );

  const getFilteredGroups = useCallback(
    (key: "is_free" | "has_free_trial") => {
      return groups
        ?.filter((group) => group[key] && !myGroupsIds?.has(group.group_id))
        .sort((a, b) => b.backtesting_score - a.backtesting_score)
        .slice(0, 12)
        .map((group) => ({
          ...group,
          id: group.group_id,
          group_type:
            key === "is_free"
              ? SignalsGroupsType.FREE
              : SignalsGroupsType.FREE_TRIAL,
        }));
    },
    [groups, myGroupsIds]
  );

  const freeGroups = useMemo(
    () => getFilteredGroups("is_free"),
    [getFilteredGroups]
  );
  const freeTrialGroups = useMemo(
    () => getFilteredGroups("has_free_trial"),
    [getFilteredGroups]
  );

  const myGroupsData = useMemo(
    () => ({
      groupType: SignalsGroupsType.MY_GROUPS,
      groups: myGroups,
      isLoading: getMyGroupsQuery.isLoading,
      isFetching: getMyGroupsQuery.isFetching,
      isSuccess: getMyGroupsQuery.isSuccess,
    }),
    [myGroups, getMyGroupsQuery]
  );

  const personalGroupsData = useMemo(
    () => ({
      groupType: SignalsGroupsType.PERSONAL,
      groups: personalGroups,
      isLoading: getMyGroupsQuery.isLoading,
      isFetching: getMyGroupsQuery.isFetching,
      isSuccess: getMyGroupsQuery.isSuccess,
    }),
    [personalGroups, getMyGroupsQuery]
  );

  const freeTrialGroupsData = useMemo(
    () => ({
      groupType: SignalsGroupsType.FREE_TRIAL,
      groups: freeTrialGroups,
      isLoading: getGroupsLoading || getMyGroupsQuery.isLoading,
      isFetching: getGroupsFetching || getMyGroupsQuery.isFetching,
      isSuccess: getGroupsSuccess,
    }),
    [freeTrialGroups, getMyGroupsQuery, getGroupsFetching, getGroupsLoading]
  );

  const freeGroupsData = useMemo(
    () => ({
      groupType: SignalsGroupsType.FREE,
      groups: freeGroups,
      isLoading: getGroupsLoading || getMyGroupsQuery.isLoading,
      isFetching: getGroupsFetching || getMyGroupsQuery.isFetching,
      isSuccess: getGroupsSuccess,
      shouldHide: !hasLeverageExchange,
    }),
    [
      freeGroups,
      hasLeverageExchange,
      getMyGroupsQuery,
      getGroupsFetching,
      getGroupsLoading,
    ]
  );

  const noExternalAccountConnected =
    !myGroups?.length && !user?.hasExternalAccount();

  const refetch = (groupType: SignalsGroupsType) => {
    try {
      if (groupType === SignalsGroupsType.MY_GROUPS) {
        getMyGroupsQuery.refetch();
      } else {
        getGroupsRefetch();
      }
    } catch {}
  };

  return {
    getMyGroupsQuery,
    myGroupsData,
    freeTrialGroupsData,
    personalGroupsData,
    freeGroupsData,
    noExternalAccountConnected,
    groupIdToNumBots,
    refetch,
  };
};

export default useFollowSignalsGroup;
